import { type ReactNode, createContext, useCallback, useContext, useState } from 'react';
import { type Theme, css } from '@emotion/react';
import { HandWaving } from '@phosphor-icons/react';
import { trpc, useMutation } from 'client/lib/trpc';
import { LoginWithGitHubButton } from 'client/pages/auth/components/LoginWithGitHubButton';
import { Banner } from 'client/components/Banner';
import { ErrorBanner } from 'client/components/Banner/ErrorBanner';
import { Dialog } from 'client/components/Dialog/Dialog';
import { Divider } from 'client/components/Divider';
import { Postcard } from 'client/components/Postcard';
import { Stack } from 'client/components/Stack';

const ClaimAccountDialog = () => {
  const [error, setError] = useState<string | null>(null);
  const { isOpen, close } = useClaimAccount();

  const claimMutation = useMutation(trpc.account.anonymousTrialClaim, {
    onMutate: () => setError(null),
    onSuccess: (data) => {
      if (data.status !== 'ok') return setError(data.status);
      window.location.href = data.url;
    },
  });

  return (
    <Dialog
      open={isOpen}
      title="Claim Account"
      description="Authenticate with GitHub to claim this account."
      icon={<HandWaving />}
      actions={null}
      width={500}
      onClose={close}
    >
      <Stack>
        <LoginWithGitHubButton
          loading={claimMutation.isLoading || (claimMutation.isSuccess && !error)}
          onClick={() => claimMutation.mutate({ path: window.location.pathname })}
        />

        {error && <ErrorBanner result={{ status: error }} />}

        <Banner>To continue, you have to sign up. Authenticate with GitHub to create an account.</Banner>
      </Stack>

      <Divider plain />

      <Postcard text={'You will keep the remaining\nfree credit when\nsigning up'} button={null} open={true} css={postcardStyle} />
    </Dialog>
  );
};

const ClaimAccountContext = createContext<{
  isOpen: boolean;
  open: VoidFunction;
  close: VoidFunction;
}>({
  isOpen: false,
  open: () => {},
  close: () => {},
});

export const ClaimAccountProvider = ({ children }: { children: ReactNode }) => {
  const [isOpen, setIsOpen] = useState(false);

  const handleOpen = useCallback(() => {
    setIsOpen(true);
    umami.track('claim-account-dialog-open', {});
  }, []);

  const handleClose = useCallback(() => {
    setIsOpen(false);
    umami.track('claim-account-dialog-close', {});
  }, []);

  return (
    <ClaimAccountContext.Provider value={{ isOpen, open: handleOpen, close: handleClose }}>
      {children}
      <ClaimAccountDialog />
    </ClaimAccountContext.Provider>
  );
};

export const useClaimAccount = () => useContext(ClaimAccountContext);

const postcardStyle = (theme: Theme) => css`
  margin: 0;
  background-color: ${theme.color.grey[60]};

  h5 > span {
    color: ${theme.color.foreground} !important;
  }
`;
